import {
	CountryEnum,
	type PartnerBillingProfileResponseDto,
	type PartnerBillingProfileUpdateDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { getFormValuesDiff } from "Infrastructure/Utilities/Forms/Helpers/getFormValuesDiff";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useUpdateBillingProfile } from "Partner/Api/Mutation/updatePartnerBillingProfileMutation";
import {
	getPartnerBillingProfileSchema,
	type PartnerBillingProfileFormModel,
} from "Partner/Components/Form/Schema/partnerBillingProfileSchema";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import Checkbox from "Shared/Components/Form/CustomFormComponent/FormCheckboxBoolean";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2 } from "@mui/material";
import { useForm } from "react-hook-form";

export type BillingProfileFormProps = {
	billingProfileData: PartnerBillingProfileResponseDto;
	partnerId: number;
	isFormDisabled?: boolean;
};

const billingProfileResources = Resources.base.partner.detail.billingProfile;
const addressInfoResources = Resources.base.partner.detail.addressInfo;

export function BillingProfileForm({
	billingProfileData,
	partnerId,
	isFormDisabled,
}: BillingProfileFormProps) {
	const { t } = useResource();

	const form = useForm<PartnerBillingProfileFormModel>({
		resolver: zodResolver(getPartnerBillingProfileSchema()),
		defaultValues: billingProfileData,
	});

	const {
		control,
		formState: { errors, dirtyFields, isDirty },
		watch,
	} = form;
	const profileType = watch("type");

	const { mutate: updateBillingProfile, isPending } = useUpdateBillingProfile(
		partnerId,
		billingProfileData?.id,
	);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitUpdate = () => {
		handleServerError((data) => {
			const changedValues = getFormValuesDiff(
				dirtyFields,
				data,
			) as PartnerBillingProfileUpdateDto;

			if (Object.keys(changedValues).length === 0) {
				return;
			}

			const prepareData = {
				...changedValues,
				type: changedValues.type ?? billingProfileData.type,
			};

			return updateBillingProfile(prepareData);
		});
	};

	return (
		<>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Checkbox
						id="vat-liable"
						name="vatLiable"
						label={t(billingProfileResources.vatLiable)}
						control={control}
						errors={errors}
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="vat-id"
						name="vatId"
						label={t(billingProfileResources.vatId)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="invoicing-name"
						name="invoicingName"
						label={t(billingProfileResources.invoicingName)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="business-id"
						name="businessId"
						label={t(billingProfileResources.businessId)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Select
						id="type"
						name="type"
						label="Type"
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						options={[
							{ label: "Individual", value: "individual" },
							{ label: "Company", value: "company" },
						]}
						disabled={isFormDisabled}
					/>
				</Grid2>

				{profileType === "individual" && (
					<>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="birth-country"
								name="birthCountry"
								label="Birth Country"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								disabled={isFormDisabled}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="birth-city"
								name="birthCity"
								label="Birth City"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								disabled={isFormDisabled}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<DatePicker
								name="birthDate"
								label="Birth Date"
								control={control}
								errors={errors}
								disabled={isFormDisabled}
							/>
						</Grid2>
					</>
				)}
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="tin"
						name="tin"
						label="TIN"
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12, sm: 6 }}>
						<Input
							id="street"
							name="address.street"
							label={t(addressInfoResources.street)}
							variant="outlined"
							control={control}
							errors={errors}
							fullWidth
							disabled={isFormDisabled}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6 }}>
						<Input
							id="city"
							name="address.city"
							label={t(addressInfoResources.city)}
							variant="outlined"
							control={control}
							errors={errors}
							fullWidth
							disabled={isFormDisabled}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6 }}>
						<Input
							id="building-number"
							name="address.buildingNumber"
							label={t(addressInfoResources.builduingNumber)}
							variant="outlined"
							control={control}
							errors={errors}
							fullWidth
							disabled={isFormDisabled}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6 }}>
						<Input
							id="post-code"
							name="address.postCode"
							label={t(addressInfoResources.postCode)}
							variant="outlined"
							control={control}
							errors={errors}
							fullWidth
							disabled={isFormDisabled}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 12, lg: 6 }}>
						<Select
							id="country"
							name="address.country"
							label={t(addressInfoResources.country)}
							fullWidth
							options={createSelectOptions(CountryEnum)}
							variant="outlined"
							control={control}
							errors={errors}
							disabled={isFormDisabled}
						/>
					</Grid2>
				</Grid2>
			</Grid2>
			<Button
				variant="contained"
				color="primary"
				onClick={handleSubmitUpdate}
				disabled={isFormDisabled || !billingProfileData?.id || !isDirty}
				loading={isPending}
				sx={{ mt: 2 }}
			>
				{t(billingProfileResources.update)}
			</Button>
		</>
	);
}
